import React, { useState, useEffect } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Switch from 'react-switch';
import { FaBars, FaTimes, FaChevronRight, FaChevronLeft, FaLanguage, FaBookOpen, FaCode } from 'react-icons/fa';
import logo from '../../assets/hansa-flex-logo.svg';
import { getLocalizedSlug, rewritePathForLanguage } from '../../lib/slugs';
import './MobileNavbar.css';

function MobileNavbar() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').substring(0, 2);
  const history = useHistory();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [path, setPath] = useState([]); // drill-down stack of parent nodes

  const slug = (key) => `/${lang}${getLocalizedSlug(key, lang)}`;
  const label = (n) => (lang === 'es' ? n.es : n.en);

  const close = () => { setOpen(false); setPath([]); };

  // Close the drawer whenever the route changes (e.g. after tapping a link)
  useEffect(() => { close(); }, [location.pathname]);

  // Lock background scroll while the drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const changeLanguage = (checked) => {
    const newLang = checked ? 'es' : 'en';
    const stripped = (location.pathname || '/').replace(/^\/(en|es)(\/|$)/i, '/');
    const newPath = `/${newLang}${rewritePathForLanguage(stripped, newLang)}`;
    i18n.changeLanguage(newLang);
    history.push(newPath + (location.search || '') + (location.hash || ''));
  };

  // Kept in step with the desktop menus (MainNavbarMenu / ES_MainNavbarMenu):
  // "HANSA-FLEX Spain" and "HANSA-FLEX Group" are two top-level entries rather
  // than one "HANSA-FLEX" wrapper, matching the production site. If the
  // desktop menu changes, change this too — the two trees are maintained by
  // hand and nothing enforces that they agree.
  const menu = [
    { en: 'HANSA-FLEX Spain', es: 'HANSA-FLEX Spain', children: [
      { en: 'Who we are', es: 'Quiénes somos', to: slug('who_we_are') },
      { en: 'Certificates', es: 'Certificaciones', to: slug('certificates') },
      { en: 'Contact us', es: 'Contacto', to: slug('contact_us') },
    ] },
    { en: 'HANSA-FLEX Group', es: 'Grupo HANSA-FLEX', children: [
      { en: 'Associations', es: 'Asociaciones', to: slug('associations') },
      { en: 'Annual Report', es: 'Informe anual', to: slug('annual_report') },
      { en: 'Certificates', es: 'Certificaciones', to: slug('certificates') },
      { en: 'History', es: 'Historia', to: slug('hf_group_history') },
      { en: 'Management', es: 'Gestión', to: slug('hf_group_management') },
      { en: 'Mission statement and values', es: 'Declaración de misión y valores', to: slug('hf_group_mission') },
      { en: 'More About HANSA-FLEX Group', es: 'Más información sobre el Grupo HANSA-FLEX', href: 'https://www.hansa-flex.de/en/company/' },
      { en: 'Responsibility and social commitment', es: 'Responsabilidad y compromiso social', children: [
        { en: 'Responsibility and social commitment', es: 'Responsabilidad y compromiso social', to: slug('responsibility_social') },
        { en: 'Donations instead of gifts', es: 'Donaciones en lugar de regalos', to: slug('donation_instead_gifts') },
        { en: 'Diversity', es: 'Diversidad', to: slug('diversity') },
      ] },
      { en: '60 years of HANSA-FLEX', es: '60 años de HANSA-FLEX', to: slug('hf_60_years') },
    ] },
    { en: 'Products', es: 'Productos', children: [
      { en: 'Approvals', es: 'Homologaciones', to: slug('approvals') },
      { en: 'Catalogues', es: 'Catálogos', to: slug('catalogues') },
      { en: 'Drive and control technology', es: 'Tecnología de accionamiento y control', children: [
        { en: 'Drive and control technology', es: 'Tecnología de accionamiento y control', to: slug('drive_and_control') },
        { en: 'Cylinders', es: 'Cilindros', to: slug('cylinder') },
        { en: 'Hydraulic motors', es: 'Motores hidráulicos', to: slug('hydraulic_motor') },
        { en: 'Measuring technology', es: 'Tecnología de medición', to: slug('measuring_tech') },
        { en: 'Pressure accumulators', es: 'Acumuladores de presión', to: slug('pressure_accumulators') },
        { en: 'Pumps', es: 'Bombas', to: slug('pumps') },
        { en: '700 bar heavy load hydraulics', es: 'Hidráulica de carga pesada de 700 bar', to: slug('seven_hundred_hydraulics') },
        { en: 'Switches and sensors', es: 'Interruptores y sensores', to: slug('switches_sensors') },
        { en: 'Valve technology', es: 'Tecnología de válvulas', children: [
          { en: 'Valve technology', es: 'Tecnología de válvulas', to: slug('valve_tech') },
          { en: 'Cartridge valves', es: 'Válvulas de cartucho', to: slug('cartridge_valve') },
          { en: 'CETOP valves', es: 'Válvulas CETOP', to: slug('cetop_valve') },
          { en: 'Pipeline installation valves', es: 'Válvulas de instalación de tuberías', to: slug('pipeline_installation_valve') },
        ] },
      ] },
      { en: 'Filtration', es: 'Filtración', to: slug('filtration') },
      { en: 'Hoses and Hose Lines', es: 'Mangueras y líneas de manguera', children: [
        { en: 'Hoses and Hose Lines', es: 'Mangueras y líneas de manguera', to: slug('hoses_and_hose_lines') },
        { en: 'Accessories', es: 'Accesorios', to: slug('hose_accessories') },
        { en: 'Hose Line Configurator', es: 'Configurador de mangueras', to: slug('hose_line_config') },
        { en: 'Extremely High Pressure Hoses', es: 'Mangueras de alta presión', to: slug('ext_high_pfm_hoses') },
        { en: 'Hydraulic Hoses', es: 'Mangueras hidráulicas', to: slug('hydraulic_hoses') },
        { en: 'Industrial Hoses', es: 'Mangueras industriales', to: slug('industrial_hoses') },
        { en: 'Metal Hoses', es: 'Mangueras metálicas', to: slug('metal_hoses') },
        { en: 'Pre-formed Hoses', es: 'Mangueras preformadas', to: slug('performed_hoses') },
        { en: 'PTFE Hoses', es: 'Mangueras de PTFE', to: slug('ptfe_hoses') },
      ] },
      { en: 'Hydraulic connection technology', es: 'Tecnología de conexión hidráulica', children: [
        { en: 'Hydraulic connection technology', es: 'Tecnología de conexión hidráulica', to: slug('hydraulic_connection_technology') },
        { en: 'Adapters', es: 'Adaptadores', to: slug('adapters') },
        { en: 'Ball valves', es: 'Válvulas de bola', to: slug('ball_valves') },
        { en: 'Couplings', es: 'Acoplamientos', to: slug('coupling') },
        { en: 'Flanges', es: 'Bridas', to: slug('flanges') },
        { en: 'Hose fittings', es: 'Racores para mangueras', to: slug('hose_fitting') },
        { en: 'Pipe fittings', es: 'Racores para tuberías', to: slug('pipe_fitting') },
      ] },
      { en: 'Pipelines', es: 'Tuberías', children: [
        { en: 'Pipelines', es: 'Tuberías', to: slug('pipelines') },
        { en: 'Expansion Joints', es: 'Juntas de dilatación', to: slug('expansion_joints') },
        { en: 'Pipe-end processing', es: 'Procesamiento de extremos de tubería', to: slug('pipeend_processing') },
        { en: 'Pipebends', es: 'Codos de tubería', to: slug('pipebends') },
      ] },
      { en: 'Pneumatics', es: 'Neumática', to: slug('pneumatics') },
      { en: 'Sealing Technology', es: 'Tecnología de sellado', to: slug('sealing_tech') },
    ] },
    { en: 'Services', es: 'Servicios', children: [
      { en: 'Assembly and Installation', es: 'Montaje e instalación', children: [
        { en: 'Assembly and Installation', es: 'Montaje e instalación', to: slug('assembly_installation') },
        { en: 'Installation of pipe and hose line systems', es: 'Instalación de sistemas de tuberías y mangueras', to: slug('installation_of_pipe_hoseline') },
        { en: 'Modernisation and optimisation', es: 'Modernización y optimización', to: slug('modernisation_optimisation') },
        { en: 'Plant project planning and management', es: 'Planificación y gestión de proyectos de planta', to: slug('plant_project_planning') },
      ] },
      { en: 'Digital Services', es: 'Servicios digitales', children: [
        { en: 'Digital Services', es: 'Servicios digitales', to: slug('digital_services') },
        { en: 'EDI and BMEcat', es: 'EDI y BMEcat', to: slug('edi_bmecat') },
        { en: 'Scanner Solutions', es: 'Soluciones de escáner', to: slug('scanner_solutions') },
      ] },
      { en: 'Engineering', es: 'Ingeniería', children: [
        { en: 'Engineering', es: 'Ingeniería', to: slug('engineering') },
        { en: 'Engineered by HANSA-FLEX', es: 'Ingeniería de HANSA-FLEX', to: slug('engineered_by_hansa_flex') },
        { en: 'Hydraulic unit manufacture', es: 'Fabricación de grupos hidráulicos', to: slug('hydraulic_unit') },
        { en: 'Prototype manufacture', es: 'Fabricación de prototipos', to: slug('prototype_manufacture') },
      ] },
      { en: 'Fluid Management', es: 'Gestión de fluidos', children: [
        { en: 'Fluid management', es: 'Gestión de fluidos', to: slug('fluid_service') },
        { en: 'Oil Analysis', es: 'Análisis de aceite', to: slug('oil_analysis') },
        { en: 'Oil Filtering and Cleaning', es: 'Filtrado y limpieza de aceite', to: slug('oil_filtering_cleaning') },
      ] },
      { en: 'Logistics solutions', es: 'Soluciones logísticas', children: [
        { en: 'Logistics solutions', es: 'Soluciones logísticas', to: slug('logistics_solutions') },
        { en: 'Kanban delivery', es: 'Entrega Kanban', to: slug('kanban_delivery') },
        { en: 'Kitting', es: 'Kitting', to: slug('kitting') },
        { en: 'Warehouse management', es: 'Gestión de almacenes', to: slug('warehouse_management') },
        { en: 'Hydraulic workshop containers', es: 'Contenedores para talleres hidráulicos', to: slug('hydraulic_workshop_containers') },
        { en: 'Works-in-works', es: 'Obras en fábrica', to: slug('works_in_works') },
      ] },
      { en: 'Mobile rapid hydraulic service', es: 'Servicio hidráulico rápido móvil', to: slug('mobile_rapid_service') },
      { en: 'Preventative maintenance', es: 'Mantenimiento preventivo', children: [
        { en: 'Preventative maintenance', es: 'Mantenimiento preventivo', to: slug('preventative_maintainance') },
        { en: 'Hose Management', es: 'Gestión de mangueras', children: [
          { en: 'Hose management', es: 'Gestión de mangueras', to: slug('hose_management') },
          { en: 'Service packages', es: 'Paquetes de servicio', to: slug('service_packages') },
          { en: 'X-CODE hose identification system', es: 'Sistema de identificación de mangueras X-CODE', to: slug('xcode') },
          { en: 'X-CODE Manager', es: 'X-CODE Manager', children: [
            { en: 'X-CODE Manager', es: 'X-CODE Manager', to: slug('xcode_manager') },
            { en: 'Licences', es: 'Licencias', to: slug('licenses') },
          ] },
        ] },
        { en: 'Inspections', es: 'Inspecciones', children: [
          { en: 'Inspections', es: 'Inspecciones', to: slug('inspection') },
          { en: 'DGUV inspection', es: 'Inspección DGUV', to: slug('dguv_inspection') },
          { en: 'Industrial hose inspection', es: 'Inspección de mangueras industriales', to: slug('industrial_hose_inspection') },
          { en: 'Lifting platforms and loading bridges', es: 'Plataformas elevadoras y puentes de carga', to: slug('lifting_platforms_loading_bridges') },
          { en: 'Pressure accumulator inspection', es: 'Inspección de acumuladores de presión', to: slug('pressure_accumulator_inspection') },
        ] },
      ] },
      { en: 'Production', es: 'Producción', children: [
        { en: 'Production', es: 'Producción', to: slug('production') },
        { en: 'Customised assembly', es: 'Montaje a medida', to: slug('customised_assembly') },
        { en: 'Series Production', es: 'Producción en serie', to: slug('series_production') },
      ] },
      { en: 'Repair and overhaul', es: 'Reparación y revisión', children: [
        { en: 'Repair and overhaul', es: 'Reparación y revisión', to: slug('repair_and_overhaul') },
        { en: 'Hydraulic cylinder repairs', es: 'Reparación de cilindros hidráulicos', to: slug('hydraulic_cylinder_repair') },
        { en: 'Pressure Accumulators and Associated Systems', es: 'Acumuladores de presión y sistemas asociados', to: slug('pressure_accumulators_systems') },
      ] },
      { en: 'Training and seminars', es: 'Formación y seminarios', to: slug('hydraulic_academy') },
    ] },
    { en: 'Technical Information', es: 'Información técnica', to: slug('technical_information') },
    { en: 'Contact', es: 'Contacto', to: slug('contact') },
    { en: 'Blogs', es: 'Blogs', to: slug('blogs') },
  ];

  const renderItem = (node, idx) => {
    if (node.children) {
      return (
        <button key={idx} className="mnav-item mnav-parent" onClick={() => setPath([...path, node])}>
          <span>{label(node)}</span><FaChevronRight />
        </button>
      );
    }
    if (node.href) {
      return (
        <a key={idx} className="mnav-item" href={node.href} target="_blank" rel="noopener noreferrer" onClick={close}>
          {label(node)}
        </a>
      );
    }
    return (
      <Link key={idx} className="mnav-item" to={node.to} onClick={close}>
        {label(node)}
      </Link>
    );
  };

  const panels = [{ title: 'HANSA-FLEX', items: menu, root: true }, ...path.map((n) => ({ title: label(n), items: n.children }))];

  return (
    <div className="mobile-navbar">
      {/* Sticky top bar */}
      <div className="mnav-bar">
        <Link to={`/${lang}/`} onClick={close}><img src={logo} className="mnav-logo" alt="HANSA-FLEX" /></Link>
        <button className="mnav-burger" aria-label="Menu" onClick={() => setOpen(true)}><FaBars /></button>
      </div>

      {/* Full-screen drawer with drill-down panels */}
      <div className={`mnav-drawer ${open ? 'is-open' : ''}`}>
        <div className="mnav-track" style={{ transform: `translateX(-${path.length * 100}vw)` }}>
          {panels.map((panel, pIdx) => (
            <div className="mnav-panel" key={pIdx}>
              <div className="mnav-head">
                {pIdx === 0 ? (
                  <img src={logo} className="mnav-logo" alt="HANSA-FLEX" />
                ) : (
                  <button className="mnav-back" onClick={() => setPath(path.slice(0, -1))}>
                    <FaChevronLeft /> <span>{panel.title}</span>
                  </button>
                )}
                <button className="mnav-close" aria-label="Close" onClick={close}><FaTimes /></button>
              </div>

              <div className="mnav-list">
                {panel.items.map(renderItem)}

                {panel.root && (
                  <div className="mnav-extra">
                    <div className="mnav-lang">
                      <FaLanguage />
                      <span className={lang !== 'es' ? 'active' : ''}>En</span>
                      <Switch
                        checked={lang === 'es'}
                        onChange={changeLanguage}
                        onColor="#4CAF50" offColor="#FF5722"
                        onHandleColor="#fff" offHandleColor="#fff"
                        handleDiameter={20} uncheckedIcon={false} checkedIcon={false}
                        height={22} width={48}
                      />
                      <span className={lang === 'es' ? 'active' : ''}>Es</span>
                    </div>
                    <a className="mnav-item" href="https://shop.hansa-flex.es/" target="_blank" rel="noopener noreferrer" onClick={close}>
                      <FaBookOpen /> {lang === 'es' ? 'Catálogo en línea' : 'Online Catalogue'}
                    </a>
                    <a className="mnav-item" href="https://my.hansa-flex.es/es_ES/login" target="_blank" rel="noopener noreferrer" onClick={close}>
                      <FaCode /> X-CODE Manager
                    </a>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MobileNavbar;
